import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import ExchangeCard from "features/portfolio/CreateAccount/ExchangeCard";
import { typography } from "@shared/common/Themes/typography";
import styled, { useTheme } from "styled-components";
import ChangeAccountTypeMessage from "features/configuration/accounts/CreateAccount/ChangeAccountTypeMessage";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Text from "common/Typography/Text";
import useSelectExchange from "@shared/features/portfolio/createAccount/useSelectExchange";
import { hexToRGB } from "@shared/utils/misc";
import { MOBILE_SEARCH } from "@shared/common/constants";
import useMedia from "utils/hooks/useMedia";
import { TABLET } from "common/constants";
import { useBottomNavigator } from "utils/hooks/misc";
import InviteExternalUser from "features/configuration/InviteExternalUsers/InviteExternalUser.tsx";
import React, { useContext, useMemo } from "react";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import ApiSlotsUsage from "features/configuration/common/ApiSlotsUsage.tsx";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context.ts";
import NewLookSelectExchange from "features/portfolio/CreateAccount/NewLook/NewLookSelectExchange";
import { phoneMinWidth } from "common/NewLook/layout.ts";

interface SelectExchangeProps {
  isOnboarding?: boolean;
  onSelectExchange?: (primaryExchange: string) => void;
  showDemoAccountOption?: boolean;
}

const SelectExchange = (props: SelectExchangeProps) => {
  const { isNewLook } = useTheme();
  if (isNewLook) return <NewLookSelectExchange {...props} />;
  return <ClassicSelectExchange {...props} />;
};

const ClassicSelectExchange = ({
  isOnboarding,
  onSelectExchange,
  showDemoAccountOption = true,
}: SelectExchangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallScreen = useMedia([`(max-width: ${TABLET})`], [true], false);
  const isVerySmallScreen = useMedia([`(max-width: 470px)`], [true], false);
  const { getIsFree } = useContext(ExchangesMetadataContext) || {};
  const {
    exchangesLoading,
    filteredExchanges: initialFilteredExchanges,
    filtersData,
  } = useSelectExchange();

  const filteredExchanges = useMemo(() => {
    return [...initialFilteredExchanges].sort(([a], [b]) => {
      if (getIsFree?.(a)) return -1;
      if (getIsFree?.(b)) return 1;
      return 0;
    });
  }, [initialFilteredExchanges, getIsFree]);

  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};

  const filters = (
    <Flex style={{ columnGap: 8, marginTop: isMobile ? 12 : 0 }}>
      <Text style={{ marginRight: 4 }}>{t("create_account.filter_by")}</Text>
      {filtersData.filtersTags.map((filterTag) => {
        return (
          <FilterButton
            key={filterTag}
            onClick={() => filtersData.onClick(filterTag)}
            $selected={filtersData.getIsSelected(filterTag)}
            $isMobile={isMobile}
          >
            {filterTag}
          </FilterButton>
        );
      })}
    </Flex>
  );

  const mobilePadding =
    isBottomNavigator && !isExternalConnectAccount
      ? { padding: `0 ${PADDING_SIDES}px` }
      : undefined;

  return (
    <Wrapper $isBottomNavigator={isBottomNavigator}>
      <div style={{ overflow: "auto" }}>
        <Flex
          style={{
            marginBottom: isMobile ? 24 : 28,
            justifyContent: isOnboarding ? "flex-end" : "space-between",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            ...mobilePadding,
          }}
        >
          {!isOnboarding && (
            <Title level={3} $weight={typography.fontWeights.SEMI_BOLD}>
              {t("create_account.select_exchange")}
            </Title>
          )}
          {filters}
        </Flex>
        <LoadingSpinnerOverlay
          isLoading={exchangesLoading}
          backgroundColor={theme.colors.layoutBgColor}
          hideContentWhileLoading={true}
        >
          <CardsWrapper
            $isOnboarding={isOnboarding}
            $isMobile={isMobile}
            $isBottomNavigator={isBottomNavigator}
            $isSmallScreen={isSmallScreen}
            $isVerySmallScreen={isVerySmallScreen}
            style={isExternalConnectAccount ? { padding: 0 } : undefined}
          >
            {filteredExchanges.map(([primaryExchange, tags], index) => {
              const columns = isOnboarding && !isSmallScreen ? 3 : 2;
              const effectiveColumns = isVerySmallScreen ? 1 : columns;
              const isSameRowAsPremium =
                !isExternalConnectAccount && index < effectiveColumns;

              return (
                <ExchangeCard
                  key={primaryExchange}
                  primaryExchange={primaryExchange}
                  tags={tags}
                  onSelectExchange={onSelectExchange}
                  isPremiumIncluded={
                    !isExternalConnectAccount && getIsFree?.(primaryExchange)
                  }
                  isSameRowAsPremium={isSameRowAsPremium}
                />
              );
            })}
          </CardsWrapper>
        </LoadingSpinnerOverlay>
      </div>
      {!isExternalConnectAccount && (
        <div>
          {showDemoAccountOption && (
            <Flex $justifyContent="center" style={mobilePadding}>
              <ChangeAccountTypeMessage
                style={{
                  marginTop: isBottomNavigator
                    ? PADDING_SIDES
                    : isOnboarding
                    ? 32
                    : 24,
                  maxWidth: isOnboarding ? 620 : "100%",
                  alignSelf: "center",
                }}
              />
            </Flex>
          )}
          <InviteExternalUser
            style={{ margin: isBottomNavigator ? "12px 12px 0" : "12px 0 0" }}
          />
          <ApiSlotsUsage
            style={{ margin: isBottomNavigator ? "12px 12px 0" : "12px 0 0" }}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default SelectExchange;

const PADDING_SIDES = 12;

const Wrapper = styled.div<{
  $isBottomNavigator?: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${(p) =>
    p.$isBottomNavigator &&
    `
      flex: 1;
      overflow: hidden;
      padding-bottom: 0;
      justify-content: space-between;
    `};
`;
const CardsWrapper = styled.div<{
  $isOnboarding?: boolean;
  $isMobile?: boolean;
  $isBottomNavigator?: boolean;
  $isSmallScreen?: boolean;
  $isVerySmallScreen?: boolean;
}>`
  display: grid;
  grid-gap: 12px;
  grid-template-columns: ${(p) =>
    p.$isOnboarding && !p.$isSmallScreen ? "1fr 1fr 1fr" : "1fr 1fr"};
  min-height: 300px;
  height: auto;

  ${(p) =>
    p.$isVerySmallScreen &&
    `
    & > *:first-child {
      grid-column: 1 / -1;
    }
  `}

  ${(p) =>
    p.$isBottomNavigator &&
    `
      padding: 0 ${PADDING_SIDES}px;
      flex-direction: column;
      grid-gap: 8px;
      min-height: 400px;
    `};
`;

const FilterButton = styled.div<{
  $selected: boolean;
  $isMobile: boolean;
}>`
  color: ${(p) => p.theme.colors.primaryGrey};
  border: ${(p) => p.theme.borders.light};
  border-color: ${(p) => p.theme.colors.lightBorderGrey};
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  ${(p) =>
    p.$selected &&
    `
      color: ${p.theme.colors.primaryBlue};
      background-color: ${p.theme.colors.lightBlue};
      border: none;
    `};

  ${(p) =>
    p.$isMobile &&
    `
    font-size: ${p.theme.typography.fontSizes.S};
  `};

  @media (min-width: ${phoneMinWidth}) {
    transition: border-color 0.4s ease-in-out;
    &:hover {
      border-color: ${(p) => p.theme.colors.primaryBlue};
    }
    ${(p) =>
      p.$selected &&
      `
      &:hover {
         background-color: ${hexToRGB(p.theme.colors.lightBlue, 0.7)};
      }
    `};
  }
`;
